import { storeToRefs } from 'pinia';
import { useMultiWhitelabelListStore } from '@/store/multi-whitelabel-slug-list/multi-whitelabel-slug-list.store';
import {
  showErrorNotification,
  showErrorNotificationWithContext,
} from '@/utils/show-notification.util';
import { useDocumentListStore } from '@/store/documents/document-list.store';
import { documentListApiToDtoMapper } from '@/mappers/documents/document-list-api-to-dto.mapper';
import { DocumentListItemResponse } from '@/models/documents/document-list.model';
import { useDocumentListFilter } from '@/hooks/documents/document-list-filter.composables';
import { useDocumentListFilterStore } from '@/store/documents/document-filter-list.store';
import { documentListFilterDtoToApi } from '@/mappers/documents/document-list-filter-dto-to-api.mapper';
import { useEnvStore } from '@/store/env.store';
import { getBrandContext } from '@/utils/get-brand-context.util';
import { delay } from 'lodash';

const IGNORED_ERROR_STATUS_CODES = [403, 401];
const ERROR_NOTIFICATION_DELAY_MS = 100;

export const useDocumentList = () => {
  const { setDocumentList, setDocumentListLoading, getDocumentListByWhitelabel } = useDocumentListStore();
  const {
    isFilterTagIdListNotEmpty,
    isFilterSegmentIdListNotEmpty,
    documentListFilterFormData,
  } = storeToRefs(useDocumentListFilterStore());

  const { multiWhitelabelSlugList } = storeToRefs(useMultiWhitelabelListStore());
  const { WL_SLUG } = storeToRefs(useEnvStore());
  const { getExternalFilterIdMap } = useDocumentListFilter();

  const showBrandErrorNotification = (
    error: unknown,
    whitelabelSlug: string,
    notificationIndex: number,
  ): void => {
    const notificationDelayMs = (notificationIndex + 1) * ERROR_NOTIFICATION_DELAY_MS;

    delay(() => {
      showErrorNotificationWithContext(error, {
        ignoredStatusCodes: IGNORED_ERROR_STATUS_CODES,
        context: getBrandContext(whitelabelSlug),
      });
    }, notificationDelayMs);
  };

  const loadDocumentList = async (): Promise<void> => {
    setDocumentListLoading(true);

    try {
      const { tagIdMap, segmentIdMap } = await getExternalFilterIdMap();
      const whitelabelSlugList = [...multiWhitelabelSlugList.value, WL_SLUG.value];

      const requestResultList: Array<PromiseSettledResult<DocumentListItemResponse[]>> = await Promise.allSettled(
        whitelabelSlugList.map((whitelabelSlug: string) => {
          const tagIdList: string[] = tagIdMap.get(whitelabelSlug) ?? [];
          const segmentIdList: string[] = segmentIdMap.get(whitelabelSlug) ?? [];

          if (
            (isFilterTagIdListNotEmpty.value && !tagIdList.length) ||
            (isFilterSegmentIdListNotEmpty.value && !segmentIdList.length)
          ) {
            return Promise.resolve([]);
          }

          return getDocumentListByWhitelabel(
            whitelabelSlug,
            documentListFilterDtoToApi(documentListFilterFormData.value, { tagIdList, segmentIdList }),
          );
        }));

      let errorNotificationIndex = 0;
      const documentResponseList: DocumentListItemResponse[] = requestResultList.flatMap(
        (
          requestResult: PromiseSettledResult<DocumentListItemResponse[]>,
          index: number,
        ): DocumentListItemResponse[] => {
          if (requestResult.status === 'fulfilled') {
            return requestResult.value;
          }

          showBrandErrorNotification(
            requestResult.reason,
            whitelabelSlugList[index],
            errorNotificationIndex,
          );
          errorNotificationIndex += 1;

          return [];
        },
      );
      const sortedDocumentResponseList: DocumentListItemResponse[] = documentResponseList.toSorted(
        (firstItem: DocumentListItemResponse, secondItem: DocumentListItemResponse) =>
          new Date(firstItem.lastPendingFileUploadedAt).getTime() -
          new Date(secondItem.lastPendingFileUploadedAt).getTime(),
      );

      setDocumentList(sortedDocumentResponseList.map(documentListApiToDtoMapper));
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setDocumentListLoading(false);
    }
  };

  return {
    loadDocumentList,
  };
};
